/**
 * The opt-in flag for the viewer's verbose telemetry CONSOLE sink, in a deliberately tiny
 * EAGER module.
 *
 * `?telemetry=true` is meant to be armable from anywhere — the worklist, a link someone
 * pastes into chat, the login page — but the only code that read the param used to live in
 * `lib/telemetry.ts`, which rides in the lazily-imported viewer chunk. On any route that
 * never loads that chunk nothing ever looked at the param, and opening a study from the
 * worklist rebuilds the viewer URL from scratch (carrying `series`, nothing else), so the
 * param was dropped before the viewer could have read it. Typing the param directly onto a
 * `/viewer/<id>` URL was the only arrangement that worked.
 *
 * Reading it here instead — from the app shell, on boot — persists the choice to
 * localStorage before the viewer chunk exists, and costs a URLSearchParams parse. This
 * module must stay free of viewer/telemetry imports: pulling either in would drag the lazy
 * chunk into the initial bundle, which is the thing being avoided.
 */
const STORAGE_KEY = 'telemetry';
/**
 * Resolve the flag from a query string, persisting an explicit `?telemetry=` choice.
 * `?telemetry` / `?telemetry=true` arm it; `?telemetry=false` disarms it; absent leaves the
 * stored value alone. Returns the resolved flag.
 *
 * Storage access is wrapped: a browser with site data blocked throws on `localStorage`, and
 * a diagnostic sink must never be the thing that breaks the app.
 */
export function syncTelemetryFlagFromUrl(search) {
    try {
        const params = new URLSearchParams(search);
        if (params.has(STORAGE_KEY)) {
            const enabled = params.get(STORAGE_KEY) !== 'false';
            localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
            return enabled;
        }
        return localStorage.getItem(STORAGE_KEY) === 'true';
    }
    catch {
        return false;
    }
}
/** Is the console sink armed right now? Reads the live URL first, then the stored flag. */
export function isTelemetryConsoleEnabled() {
    if (typeof window === 'undefined')
        return false;
    return syncTelemetryFlagFromUrl(window.location.search);
}
